import React, { Fragment, useContext, useRef, useState } from 'react';
import moment from 'moment';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { InvoiceStatus } from '../../../enums/InvoiceStatusEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  IContact,
  IInvoice,
  IMutation,
  useConfirmInvoiceMutation,
  useSendInvoiceForApprovalMutation,
  useSendInvoiceToCustomerMutation
} from '../../../generated/graphql';
import { DATE_TIME_FORMATS } from '../../../utils/constants';
import { canUserEdit } from '../../../utils/rightUtils';
import { getInvoiceProductInfoDto } from '../InvoiceList';
import { useReactToPrint } from 'react-to-print';
import html2canvas from '@nidi/html2canvas';
import jsPDF from 'jspdf';
import { Box, Button, Divider, Link, Paper } from '@mui/material';
import Loader from '../../Common/Loader';
import preview from '../../../assets/images/actions/center_focus_strong.svg';
import { Colors } from '../../../utils/theme';
import Invoice from './Invoice';
import InvoicePreview from './InvoicePreview';
import InputComponent from '../../Common/Fields/InputComponent';
import { CheckCircle, Print, Send, Error } from '@mui/icons-material';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import CcInput from '../CcInput';
import EpraChip from '../../Common/Fields/EpraChip';

interface IProps {
  children?: React.ReactNode;
  setPreview?: () => void;
  editInvoice?: (invoice: IInvoice) => void;
  dbContact: IContact;
}

const InvoiceStep: React.FunctionComponent<IProps> = ({
  editInvoice,
  children,
  setPreview,
  dbContact
}: IProps) => {
  const buttonWidth = '130px';
  const { state, dispatch } = useContext(AppContext);
  const [hover, setHover] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const { selectedInvoice } = state;
  const isCredit = selectedInvoice?.invoice_full?.is_credit;
  const isConfirmed = selectedInvoice?.status?.includes(InvoiceStatus.Confirmed);
  const isNew = selectedInvoice?.status === InvoiceStatus.New;
  const [confirmInvoice, { loading }] = useConfirmInvoiceMutation();
  const [sendInvoiceForApproval, sendInvoiceForApprovalResult] =
    useSendInvoiceForApprovalMutation();
  const [sendInvoiceToCustomer, sendInvoiceToCustomerResult] = useSendInvoiceToCustomerMutation();
  const [openCcInput, setOpenCcInput] = useState(false);
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const invoiceRef = useRef(null);
  const canEdit = canUserEdit(state.user, Types.Invoice);
  const disableSend =
    !canEdit ||
    !isConfirmed ||
    !selectedInvoice?.contact ||
    !selectedInvoice?.contact.email ||
    selectedInvoice?.contact.email.length === 0;

  const formatInputDate = (input: string | null | undefined) => {
    if (input) {
      return moment(parseInt(input)).format(DATE_TIME_FORMATS.DATE);
    }
    return '';
  };

  const handlePreviewClicked = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setOpenPreview(!openPreview);
    if (setPreview) {
      setPreview();
    }
  };

  const handleConfirmClicked = () => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: 'Are you sure you want to confirm this invoice?',
        agreeAction: handleConfirmInvoice
      }
    });
  };

  const handleConfirmInvoice = () => {
    if (selectedInvoice) {
      confirmInvoice({
        variables: {
          id: parseInt(selectedInvoice.id as string)
        },
        update(_, { data }) {
          if (data?.result) {
            const invoice = data.result as IInvoice;
            dispatch({
              type: Actions.SetInvoice,
              payload: {
                invoice,
                productInfo: getInvoiceProductInfoDto(invoice)
              }
            });
            if (editInvoice) {
              editInvoice(invoice);
            }
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `You have successfully confirmed invoice`
              }
            });
          }
        }
      })
        .catch((e) => {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'error',
              text: e.message,
              autoHide: 6000
            }
          });
        })
        .finally(() => {
          dispatch({
            type: Actions.HideConfirmation
          });
        });
    }
  };

  const handlePrint = useReactToPrint({
    content: () => invoiceRef.current
  });

  const sendForApprove = () => {
    handleSendingInvoice(sendInvoiceForApproval, true);
  };

  const sendToCustomer = () => {
    handleSendingInvoice(sendInvoiceToCustomer, false);
  };

  const handleSendingInvoice = (sendMethod: CallableFunction, isForApproval: boolean) => {
    //SVG icons hack for canvas capturing
    const svgElements = document.body.querySelectorAll('svg');
    svgElements.forEach((item) => {
      item.setAttribute('width', item.getBoundingClientRect().width.toString());
      if (item.className.animVal.indexOf('invoice-confirmed-circle-icon') > -1) {
        item.setAttribute('fill', 'rgb(130, 205, 43)');
      } else {
        item.setAttribute('fill', 'rgb(105, 170, 243)');
      }
    });
    html2canvas(invoiceRef.current as unknown as HTMLElement, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      // pdf.save('download.pdf');
      const base64 = pdf.output('datauristring');
      const vars = {
        id: parseInt(selectedInvoice?.id as string),
        pdf_content: base64,
        cc_emails: ccRecipients
      };
      sendMethod({
        variables: vars,
        update(
          _: 'Mutation',
          {
            data
          }: {
            data: {
              result: IMutation['sendInvoiceToCustomer'];
            };
          }
        ) {
          if (data?.result) {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `You have successfully sent invoice ${
                  isForApproval ? 'for approval' : 'to customer'
                }`
              }
            });
          } else {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'error',
                text: `Invoice not sent`
              }
            });
          }
        }
      })
        .catch((e: Error) => {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'error',
              text: e.message,
              autoHide: 6000
            }
          });
        })
        .finally(() => {
          dispatch({
            type: Actions.HideConfirmation
          });
        });
    });
  };

  return (
    <Box>
      <Box
        sx={{
          pt: '6px',
          pl: '17%',
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: '8.24%'
        }}
      >
        <Box sx={{ gridColumn: 'span 2' }}>
          {hover && (
            <Box
              component="img"
              src={preview}
              onClick={handlePreviewClicked}
              sx={{
                position: 'absolute',
                zIndex: 2,
                top: '42%',
                left: '24%',
                pointerEvents: 'none'
              }}
            />
          )}
          <Paper
            id="invoice-preview-paper"
            data-testid="invoice-preview-paper"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={handlePreviewClicked}
            sx={{
              width: '100%',
              height: '410px',
              boxShadow: '0px 2px 5px ' + Colors.third + '66',
              cursor: 'pointer'
            }}
            className={hover ? 'blurred-invoice-paper' : ''}
          >
            {children || <Invoice dbContact={dbContact} />}
          </Paper>
        </Box>
        <Box sx={{ gridColumn: 'span 3', marginTop: isConfirmed ? '0px' : '33px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '30% 70%'
            }}
          >
            <Box
              component="p"
              id="invoice-title"
              data-testid="invoice-title"
              sx={{
                color: isCredit ? Colors.orange : Colors.main,
                fontWeight: 'bold',
                fontSize: '20px',
                fontFamily: 'Segoe UI',
                gridColumn: 'span 2'
              }}
            >
              {isCredit ? 'CREDIT' : 'INVOICE'}
              {isConfirmed && ` ${selectedInvoice?.invoice_full?.number ?? ''}`}
            </Box>
            {isCredit && (
              <Box
                component="p"
                sx={{ gridColumn: 'span 2', mt: '-25px', fontSize: '14px', color: Colors.gray }}
              >
                /Ref: INVOICE {selectedInvoice?.invoice_full?.debit_invoice_nr}/
              </Box>
            )}
            {isConfirmed && (
              <InputComponent
                id="invoice-date-paid"
                data-testid="invoice-date-paid"
                label="Date Paid"
                value={formatInputDate(selectedInvoice?.invoice_full?.date_paid)}
                disabled
                withoutBackground
                inputLabelProps={{
                  shrink: true,
                  style: { top: '-7px', color: Colors.main, fontFamily: 'Overpass' }
                }}
                additionalStyle={{
                  p: 0,
                  mt: '24px',
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: Colors.main
                  }
                }}
              />
            )}
            {isConfirmed && (
              <InputComponent
                id="invoice-due-date"
                data-testid="invoice-due-date"
                label="Due Date"
                value={formatInputDate(selectedInvoice?.invoice_full?.due_date)}
                disabled
                withoutBackground
                inputLabelProps={{
                  shrink: true,
                  style: { top: '-7px', color: Colors.main, fontFamily: 'Overpass' }
                }}
                additionalStyle={{
                  pl: 0,
                  mt: '24px',
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: Colors.main
                  }
                }}
              />
            )}
            {isConfirmed && (
              <Divider
                sx={{
                  backgroundColor: isConfirmed ? Colors.gray : Colors.third,
                  mt: '26px',
                  gridColumn: 'span 2'
                }}
              />
            )}
            <Button
              id="invoice-send-for-approval-button"
              data-testid="invoice-send-for-approval-button"
              component={Link}
              disabled={!canEdit || isNew}
              onClick={sendForApprove}
              sx={{
                mt: '31px',
                fontSize: 14,
                fontWeight: 'bold',
                textTransform: 'none',
                color: Colors.skyBlue + 'CC',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: 'underline',
                width: '145px'
              }}
            >
              <Send
                sx={{
                  fontSize: 'medium',
                  color: !canEdit || isNew ? Colors.gray : Colors.skyBlue,
                  mr: '3px',
                  mb: '6px',
                  transform: 'rotate(-48deg)'
                }}
              />
              Send for approval
            </Button>
            <Button
              id="invoice-print-button"
              data-testid="invoice-print-button"
              component={Link}
              onClick={handlePrint}
              sx={{
                mt: '31px',
                fontSize: 14,
                fontWeight: 'bold',
                textTransform: 'none',
                color: Colors.skyBlue + 'CC',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: 'underline',
                width: '60px'
              }}
            >
              <Print
                sx={{
                  fontSize: 'medium',
                  color: Colors.skyBlue,
                  mr: '3px'
                }}
              />
              Print
            </Button>
            <Divider
              sx={{
                backgroundColor: isConfirmed ? Colors.gray : Colors.third,
                mt: '26px',
                gridColumn: 'span 2'
              }}
            />
            <CancelButton
              id="confirm-invoice-button"
              data-testid="confirm-invoice-button"
              disabled={!canEdit || isNew || isConfirmed}
              onClick={handleConfirmClicked}
              text="confirm"
              sx={{
                mt: '30px',
                width: buttonWidth,
                '&:hover': {
                  background: (theme) => theme.palette.primary.main,
                  color: Colors.white,
                  border: 'none'
                },
                '&:disabled': {
                  borderColor: (theme) => theme.palette.grey[300]
                }
              }}
            />
            <InputComponent
              id="invoice-status"
              label="Status"
              value={isNew ? 'unsaved' : isConfirmed ? 'confirmed' : 'pending'}
              disabled
              withoutBackground
              additionalStyle={{
                mt: '39px',
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: isNew
                    ? Colors.warning
                    : isConfirmed
                    ? Colors.green
                    : Colors.orange,
                  fontFamily: 'Overpass'
                }
              }}
              inputLabelProps={{
                style: { top: '-12px', left: '6px' }
              }}
              startAdornment={
                !isNew &&
                (isConfirmed ? (
                  <CheckCircle
                    sx={{
                      mr: '7px',
                      color: Colors.green
                    }}
                  />
                ) : (
                  <Error
                    sx={{
                      mr: '7px',
                      color: Colors.orange
                    }}
                  />
                ))
              }
            />
            <SaveButton
              id="send-invoice-button"
              data-testid="send-invoice-button"
              disabled={disableSend}
              onClick={sendToCustomer}
              text="send invoice"
              sx={{
                mt: '30px',
                width: buttonWidth,
                ml: 0,
                background: (theme) => theme.palette.primary.main,
                '&:hover': {
                  background: 'white',
                  border: '1px solid ' + Colors.main,
                  color: Colors.main,
                  boxShadow: 'none'
                },
                '&:disabled': {
                  background: (theme) => theme.palette.grey[300]
                }
              }}
            />
            <InputComponent
              id="invoice-email-receiver"
              data-testid="invoice-email-receiver"
              label="This invoice wii be sent to:"
              value={selectedInvoice?.contact?.email ?? ''}
              disabled
              withoutBackground
              additionalStyle={{
                mt: '39px',
                pl: '2%'
              }}
              inputLabelProps={{
                style: { top: '-12px', left: '7px' }
              }}
            />
            {!disableSend && (
              <Fragment>
                <CancelButton
                  id="add-cc"
                  onClick={() => setOpenCcInput(true)}
                  text="add cc"
                  sx={{ maxWidth: buttonWidth }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {ccRecipients.map((recipient, index) => (
                    <EpraChip
                      key={`${recipient}-chip-${index}`}
                      id={`${recipient}-chip-${index}`}
                      label={recipient}
                      onDelete={() => setCcRecipients(ccRecipients.filter((r) => r !== recipient))}
                    />
                  ))}
                </Box>
              </Fragment>
            )}
          </Box>
        </Box>
      </Box>
      {openPreview && (
        <InvoicePreview
          handleClose={() => {
            if (setPreview) {
              setPreview();
            }
            setOpenPreview(false);
          }}
        >
          {children || <Invoice fullscreen />}
        </InvoicePreview>
      )}
      {openCcInput && (
        <CcInput
          onClose={() => setOpenCcInput(false)}
          recientsInput={ccRecipients}
          setCcRecipients={(rec) => setCcRecipients(rec)}
        />
      )}
      <Loader
        loading={
          loading || sendInvoiceForApprovalResult.loading || sendInvoiceToCustomerResult.loading
        }
      />
      <Box sx={{ overflow: 'hidden', height: 0 }}>
        <Box ref={invoiceRef} style={{ height: '1150px', width: '815px' }}>
          <Invoice fullscreen />
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceStep;
